import React, { createContext, useContext, useState, useEffect } from 'react';
import { isAdminLoggedIn } from '../utils/auth';
import api from '../utils/api';

const AdminEditContext = createContext();

export const useAdminEdit = () => {
  const context = useContext(AdminEditContext);
  if (!context) {
    throw new Error('useAdminEdit must be used within AdminEditProvider');
  }
  return context;
};

export const AdminEditProvider = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [customizations, setCustomizations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only enable edit mode if admin is logged in
    if (isAdminLoggedIn()) {
      loadCustomizations();
    } else {
      setLoading(false);
    }
  }, []);

  const loadCustomizations = async () => {
    try {
      const response = await api.get('/admin/customizations');
      setCustomizations(response.data || {});
    } catch (error) {
      console.error('Error loading customizations:', error);
      // If endpoint doesn't exist, start with empty customizations
      setCustomizations({});
    } finally {
      setLoading(false);
    }
  };

  const saveCustomization = async (key, value) => {
    const newCustomizations = { ...customizations, [key]: value };
    setCustomizations(newCustomizations);
    
    try {
      await api.post('/admin/customizations', { key, value });
    } catch (error) {
      console.error('Error saving customization:', error);
      // Revert on error
      setCustomizations(customizations);
    }
  };

  const restoreDefaults = async () => {
    if (window.confirm('Are you sure you want to restore all customizations to default? This will remove all custom text and colors.')) {
      try {
        await api.delete('/admin/customizations');
        setCustomizations({});
        window.location.reload(); // Reload to show defaults
      } catch (error) {
        console.error('Error restoring defaults:', error);
      }
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const getCustomization = (key, defaultValue) => {
    return customizations[key] !== undefined ? customizations[key] : defaultValue;
  };

  return (
    <AdminEditContext.Provider
      value={{
        isEditMode,
        toggleEditMode,
        customizations,
        saveCustomization,
        getCustomization,
        restoreDefaults,
        loading,
        isAdmin: isAdminLoggedIn()
      }}
    >
      {children}
    </AdminEditContext.Provider>
  );
};
